import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { Calendar, Clock, MapPin, User, Mail, Building } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UserDetailsModal = ({ user, isOpen, onClose }) => {
  const [bookingHistory, setBookingHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user?.id) {
      fetchUserBookingHistory();
    }
  }, [isOpen, user?.id]);

  const fetchUserBookingHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users/${user.id}/bookings`);
      setBookingHistory(response.data);
    } catch (error) {
      console.error('Error fetching user booking history:', error);
      toast.error('Kunne ikke indlæse booking historik');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: da });
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: da });
  };

  const getBookingStats = () => {
    const totalBookings = bookingHistory.length;
    const today = new Date().toISOString().split('T')[0];
    const upcomingBookings = bookingHistory.filter(booking => booking.date >= today);
    const pastBookings = bookingHistory.filter(booking => booking.date < today);
    
    return {
      total: totalBookings,
      upcoming: upcomingBookings.length,
      past: pastBookings.length
    };
  };

  if (!user) return null;

  const stats = getBookingStats();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-3">
            <User className="w-6 h-6" />
            Bruger Detaljer
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* User Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Bruger Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-slate-600" />
                    <div>
                      <p className="text-sm text-slate-600">Navn</p>
                      <p className="font-semibold">{user.name || 'Ikke angivet'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-600" />
                    <div>
                      <p className="text-sm text-slate-600">Email</p>
                      <p className="font-semibold">{user.email}</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Building className="w-4 h-4 text-slate-600" />
                    <div>
                      <p className="text-sm text-slate-600">Organisation</p>
                      <p className="font-semibold">{user.organization || 'Ikke angivet'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-slate-600" />
                    <div>
                      <p className="text-sm text-slate-600">Oprettet</p>
                      <p className="font-semibold">{formatDateTime(user.created_at)}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 flex gap-3">
                <Badge variant={user.role === 'ADMIN' ? 'default' : 'secondary'}>
                  {user.role}
                </Badge>
                <Badge variant={user.is_active ? 'default' : 'destructive'} 
                       className={user.is_active ? 'bg-green-600 hover:bg-green-700' : ''}>
                  {user.is_active ? 'Aktiv' : 'Inaktiv'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Booking Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.total}</p>
                    <p className="text-sm text-slate-600">Total Bookinger</p>
                  </div>
                  <Calendar className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.upcoming}</p>
                    <p className="text-sm text-slate-600">Kommende</p>
                  </div>
                  <Clock className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.past}</p>
                    <p className="text-sm text-slate-600">Tidligere</p>
                  </div>
                  <MapPin className="w-8 h-8 text-slate-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Booking Historik ({bookingHistory.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-slate-600 mt-2">Indlæser booking historik...</p>
                </div>
              ) : bookingHistory.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {bookingHistory.map((booking) => (
                    <div key={booking.id} className="p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold">{booking.title}</h4>
                            <Badge variant="outline">{booking.resource}</Badge>
                            <Badge variant={new Date(booking.date) >= new Date() ? 'default' : 'secondary'}>
                              {new Date(booking.date) >= new Date() ? 'Kommende' : 'Tidligere'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(booking.date)}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {booking.start_time} - {booking.end_time}
                            </div>
                          </div>
                          {booking.notes && (
                            <p className="text-sm text-slate-600 mt-2">{booking.notes}</p>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          Oprettet: {formatDateTime(booking.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600">Ingen bookinger fundet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};